// The single hook into ApiService: wrap the singleton in a Proxy. A call to a routed method that has a NomOS
// implementation goes to the registry; everything else (and everything when nothing is routed) hits the original.
import { isRouted, type RouteSources } from './switch';
import { nomosRegistry, type NomosRegistry } from './registry';

const SESSION_METHODS = new Set(['login', 'chooseTenant', 'logout', 'acceptInvitation']);

/** What a user sees for a screen NomOS does not serve yet (a NomosGapError). The gap's own text names the missing
 *  NomOS piece for developers: it goes to the console, not the page. */
export const NOT_AVAILABLE_YET = 'Not available yet';

export function withNomosSwitch<T extends object>(
  target: T,
  opts: { registry?: NomosRegistry; sources?: () => RouteSources | undefined } = {},
): T {
  const registry = opts.registry ?? nomosRegistry;
  return new Proxy(target, {
    get(t, prop, receiver) {
      if (typeof prop === 'string' && registry[prop] && isRouted(prop, opts.sources?.())) {
        const impl = registry[prop];
        return async (...args: unknown[]) => {
          try {
            const out = await (impl as (...a: unknown[]) => Promise<unknown>)(...args);
            // Per-account browser state (dashboard preload, customer context) resets on the same signal the
            // Sentinel login/logout give: with several tenants (B7) a new session can be a different customer.
            if (SESSION_METHODS.has(prop)) (t as unknown as { sessionChanged?: () => void }).sessionChanged?.();
            return out;
          } catch (e) {
            // same 401 contract as the axios interceptor
            if ((e as { response?: { status?: number } })?.response?.status === 401) {
              (t as unknown as { handleUnauthorized?: () => void }).handleUnauthorized?.();
            }
            if ((e as Error)?.name === 'NomosGapError') {
              console.info(`${prop}: ${(e as Error).message}`);
              throw Object.assign(new Error(NOT_AVAILABLE_YET), { name: 'NomosGapError', cause: e });
            }
            throw e;
          }
        };
      }
      return Reflect.get(t, prop, receiver);
    },
  });
}
